import React from 'react';
import {
  C,
  CARD_W,
  CIRCLE_R,
  PLUS_R,
  BRANCH_STEM,
  CARD_GAP,
} from '../flowCanvas.constants';
import { IMAGE_OVERLAY_CARD_H } from './ImageOverlayCardNode';
import ParticleStackCardNode from './ParticleStackCardNode';

export default function ParticleStackNode({
  step,
  stepX,
  stepCY,
  parentStep,
  parentStepX,
  parentStepY,
  items,
  activeStepId,
  activeGraphicIndex,
  assetFolder,
  campaignId,
  onItemClick,
  onAddItemClick,
  onRemoveItem,
  variant = 'image',
  titleBase,
  showFireworksVariancePreview = false,
}) {
  const parentRadius = parentStep?.nodeConfig?.nodeStyle === 'infoButton' ? PLUS_R : CIRCLE_R;
  const hasParent = parentStepX !== null && parentStepX !== undefined;
  const count = Math.max(items.length, 1);
  const totalH = count * (IMAGE_OVERLAY_CARD_H + CARD_GAP) - CARD_GAP;
  const cardCentreX = stepX;
  const cardLeftX = cardCentreX - CARD_W / 2;
  const sourceX = hasParent ? parentStepX + parentRadius : cardLeftX - BRANCH_STEM;
  const sourceY = parentStepY ?? stepCY;
  // Center the stack on stepCY (which equals the parent node's Y) so the entry arm
  // always lands at the visual midpoint of all cards — matching how ImageStackNode works.
  const stackTopY = stepCY - totalH / 2;
  const stackMidY = stepCY;
  const branchMidX = Math.max(sourceX + BRANCH_STEM, (sourceX + cardLeftX) / 2);
  const plusX = (sourceX + branchMidX) / 2;
  const plusY = sourceY;
  const firstCardY = stackTopY + IMAGE_OVERLAY_CARD_H / 2;
  const lastCardY = stackTopY + (count - 1) * (IMAGE_OVERLAY_CARD_H + CARD_GAP) + IMAGE_OVERLAY_CARD_H / 2;
  const branchColor = items.length > 0 ? C.purple : C.orange;
  const addButtonLabel = step.customisationConfig?.meta?.addButtonLabel || (variant === 'colour' ? 'Add Colour' : 'Add Image');
  const subtitle = variant === 'colour' ? 'Colour frequency' : 'Custom image';
  const totalFreq = items.reduce((sum, item) => sum + (Number(item?.frequency) || 0), 0) || 0;
  const configuredItemCount = items.reduce((sum, item) => {
    if (variant === 'colour') {
      return sum + (typeof item?.colour === 'string' && item.colour.trim() ? 1 : 0);
    }
    const imageField = item?.image;
    if (typeof imageField === 'string') return sum + (imageField.trim() ? 1 : 0);
    if (imageField && typeof imageField === 'object') {
      return sum + (imageField.filename || imageField.srcUrl ? 1 : 0);
    }
    return sum;
  }, 0);

  return (
    <>
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          <line
            x1={sourceX}
            y1={sourceY}
            x2={branchMidX}
            y2={sourceY}
            stroke={branchColor}
            strokeWidth="1.5"
            strokeOpacity="0.78"
            strokeLinecap="square"
          />
          {Math.abs(sourceY - stackMidY) > 2 && (
            <line
              x1={branchMidX}
              y1={sourceY}
              x2={branchMidX}
              y2={stackMidY}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.78"
              strokeLinecap="square"
            />
          )}
          {(items.length > 1 || Math.abs(stackMidY - firstCardY) > 2 || Math.abs(stackMidY - lastCardY) > 2) && (
            <line
              x1={branchMidX}
              y1={Math.min(firstCardY, stackMidY)}
              x2={branchMidX}
              y2={Math.max(lastCardY, stackMidY)}
              stroke={branchColor}
              strokeWidth="1.5"
              strokeOpacity="0.5"
            />
          )}
          {(items.length > 0 ? items : [null]).map((_, i) => {
            const cy = stackTopY + i * (IMAGE_OVERLAY_CARD_H + CARD_GAP) + IMAGE_OVERLAY_CARD_H / 2;
            return (
              <React.Fragment key={`spur-${i}`}>
                <line
                  x1={branchMidX}
                  y1={cy}
                  x2={cardLeftX}
                  y2={cy}
                  stroke={branchColor}
                  strokeWidth="1.5"
                  strokeOpacity="0.78"
                  strokeLinecap="square"
                />
                <circle cx={cardLeftX} cy={cy} r="3" fill={branchColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
          <text
            x={plusX}
            y={plusY - PLUS_R - 8}
            textAnchor="middle"
            fontSize="9.5"
            fontWeight="600"
            fill={C.greenText}
            style={{ userSelect: 'none' }}
          >
            {addButtonLabel}
          </text>
        </g>
      )}

      {hasParent && (
        <g onClick={onAddItemClick} style={{ cursor: 'pointer' }}>
          <circle cx={plusX} cy={plusY} r={PLUS_R} fill={C.greenText} />
          <text
            x={plusX}
            y={plusY + 1}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="16"
            fontWeight="300"
            fill="#fff"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >+
          </text>
        </g>
      )}

      {items.length > 0 ? items.map((item, index) => {
        const cardY = stackTopY + index * (IMAGE_OVERLAY_CARD_H + CARD_GAP) + IMAGE_OVERLAY_CARD_H / 2;
        const isSelected = activeStepId === step.stepId && activeGraphicIndex === index;
        const frequency = Number(item?.frequency) || 0;
        const frequencyPct = totalFreq > 0 ? Math.round((frequency / totalFreq) * 100) : 0;
        const canDelete = configuredItemCount > 1;
        return (
          <ParticleStackCardNode
            key={`${step.stepId}-${index}`}
            item={item}
            index={index}
            selected={isSelected}
            onClick={() => onItemClick(index)}
            onDelete={onRemoveItem && canDelete ? () => onRemoveItem(index) : null}
            itemCount={items.length}
            configuredItemCount={configuredItemCount}
            overrideX={cardCentreX}
            overrideY={cardY}
            assetFolder={assetFolder}
            campaignId={campaignId}
            variant={variant}
            title={`${titleBase} ${index + 1}`}
            subtitle={subtitle}
            frequencyPct={frequencyPct}
            showFireworksVariancePreview={showFireworksVariancePreview}
          />
        );
      }) : (
        <g key="empty" onClick={onAddItemClick} style={{ cursor: 'pointer' }}>
          <rect x={cardCentreX - CARD_W / 2 + 2} y={stepCY - IMAGE_OVERLAY_CARD_H / 2 + 4}
            width={CARD_W} height={IMAGE_OVERLAY_CARD_H} rx="8" fill="#000" fillOpacity="0.3" />
          <rect x={cardCentreX - CARD_W / 2} y={stepCY - IMAGE_OVERLAY_CARD_H / 2}
            width={CARD_W} height={IMAGE_OVERLAY_CARD_H} rx="8"
            fill={C.card} stroke={C.orange} strokeWidth="1.5" strokeDasharray="5 3" />
          <text x={cardCentreX} y={stepCY - 18} textAnchor="middle" fontSize="11" fontWeight="600" fill={C.orange}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>{variant === 'colour' ? 'No colours yet' : 'No images yet'}</text>
          <text x={cardCentreX} y={stepCY + 6} textAnchor="middle" fontSize="20" fontWeight="300" fill={C.greenText}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
          <text x={cardCentreX} y={stepCY + 28} textAnchor="middle" fontSize="9" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>{variant === 'colour' ? 'Click to add a colour' : 'Click to add an image'}</text>
        </g>
      )}
    </>
  );
}
